import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { createShiftSwap, decideShiftSwap, listEmployees, listPlannedSchedules, listShiftSwaps } from '../../api/hr';
import { usePermissions } from '../../context/PermissionContext';
import { useBranch } from '../../context/BranchContext';
import {
  Button,
  ClickableRow,
  Drawer,
  DrawerField,
  EmptyState,
  ErrorNote,
  FieldLabel,
  ListToolbar,
  LoadingNote,
  PageHeader,
  Pagination,
  Select,
  StatusBadge,
  SummaryStrip,
  TextInput,
  formatDateTime,
  statusCounts,
  tableStyle,
  tdStyle,
  thStyle,
  usePagination,
} from '../../components/common';
import type { Employee, Schedule, ShiftSwap, LeaveRequestStatus } from '../../types/domain';

const SWAP_STATUSES: LeaveRequestStatus[] = ['REQUESTED', 'APPROVED', 'REJECTED', 'CANCELLED'];

/**
 * Shift Swap workspace (FASE 9, migration 0040). Branch-scoped. Requesting
 * a swap needs an existing PLANNED schedule (same constraint as Attendance
 * — no schedule-authoring UI in this slice). Approving reassigns the
 * schedule server-side (fn_decide_shift_swap) — this page never mutates
 * `schedules` directly.
 *
 * STATUS: IMPLEMENTED_UNVERIFIED — see docs/data-dictionary.md NEEDS_DECISION log.
 */
export default function ShiftSwapsPage() {
  const { hasPermission } = usePermissions();
  const { activeBranchId } = useBranch();
  const [swaps, setSwaps] = useState<ShiftSwap[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [selected, setSelected] = useState<ShiftSwap | null>(null);
  const [decidingId, setDecidingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const canCreate = hasPermission('shift_swap', 'create');
  const canApprove = hasPermission('shift_swap', 'approve');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [swapRows, empRows] = await Promise.all([listShiftSwaps(activeBranchId ?? undefined), listEmployees(activeBranchId ?? undefined)]);
      setSwaps(swapRows);
      setEmployees(empRows);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBranchId]);

  const employeeName = (id: string) => employees.find((e) => e.employee_id === id)?.legal_name ?? '—';

  const handleDecide = async (swapId: string, approve: boolean) => {
    setActionError(null);
    setDecidingId(swapId);
    try {
      await decideShiftSwap(swapId, approve);
      await load();
      setSelected(null);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setDecidingId(null);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return swaps.filter((s) => {
      if (q && !employeeName(s.requesting_employee_id).toLowerCase().includes(q) && !employeeName(s.covering_employee_id).toLowerCase().includes(q)) return false;
      if (statusFilter !== 'ALL' && s.status !== statusFilter) return false;
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swaps, search, statusFilter, employees]);
  const { pageRows, page, pageCount, setPage, start, pageSize, total } = usePagination(filtered, 15);

  return (
    <div>
      <PageHeader title="Tukar Shift" subtitle="Pengajuan dan persetujuan tukar jadwal kerja. Migrasi 0040." />
      {canCreate && (
        <Button variant="primary" onClick={() => setShowForm((v) => !v)} style={{ marginBottom: 16 }} disabled={!activeBranchId}>
          {showForm ? 'Batal' : '+ Ajukan Tukar Shift'}
        </Button>
      )}
      {showForm && canCreate && (
        <NewSwapForm
          employees={employees}
          onCreated={() => {
            setShowForm(false);
            void load();
          }}
        />
      )}
      {actionError && <ErrorNote text={actionError} />}
      {loading && <LoadingNote />}
      {error && <ErrorNote text={error} />}
      {!loading && !error && (
        <>
          <SummaryStrip
            items={(() => {
              const c = statusCounts(swaps, SWAP_STATUSES);
              return [
                { label: 'Total', value: swaps.length },
                { label: 'Pending', value: c.REQUESTED, tone: 'warning' as const },
                { label: 'Disetujui', value: c.APPROVED, tone: 'success' as const },
                { label: 'Ditolak', value: c.REJECTED, tone: 'danger' as const },
              ];
            })()}
          />
          <ListToolbar
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Cari nama karyawan..."
            resultCount={total}
            filters={
              <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ width: 160 }}>
                <option value="ALL">Semua status</option>
                <option value="REQUESTED">Pending</option>
                <option value="APPROVED">Disetujui</option>
                <option value="REJECTED">Ditolak</option>
                <option value="CANCELLED">Dibatalkan</option>
              </Select>
            }
          />
          {swaps.length === 0 && <EmptyState text="Belum ada pengajuan tukar shift." />}
          {swaps.length > 0 && total === 0 && <EmptyState text="Tidak ada pengajuan yang cocok dengan pencarian/filter." />}
          {total > 0 && (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Peminta</th>
                  <th style={thStyle}>Pengganti</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}></th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((s) => (
                  <ClickableRow key={s.swap_id} onClick={() => setSelected(s)}>
                    <td style={tdStyle}>{employeeName(s.requesting_employee_id)}</td>
                    <td style={tdStyle}>{employeeName(s.covering_employee_id)}</td>
                    <td style={tdStyle}>
                      <StatusBadge status={s.status} />
                    </td>
                    <td style={tdStyle}>
                      {canApprove && s.status === 'REQUESTED' && (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <Button
                            variant="primary"
                            loading={decidingId === s.swap_id}
                            onClick={(e) => {
                              e.stopPropagation();
                              void handleDecide(s.swap_id, true);
                            }}
                          >
                            Setujui
                          </Button>
                          <Button
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              void handleDecide(s.swap_id, false);
                            }}
                          >
                            Tolak
                          </Button>
                        </div>
                      )}
                    </td>
                  </ClickableRow>
                ))}
              </tbody>
            </table>
          )}
          <Pagination page={page} pageCount={pageCount} setPage={setPage} start={start} pageSize={pageSize} total={total} />
        </>
      )}
      <Drawer open={!!selected} onClose={() => setSelected(null)} title="Detail Tukar Shift">
        {selected && (
          <div>
            <DrawerField label="Peminta" value={employeeName(selected.requesting_employee_id)} />
            <DrawerField label="Pengganti" value={employeeName(selected.covering_employee_id)} />
            <DrawerField label="Status" value={<StatusBadge status={selected.status} />} />
            <DrawerField label="Alasan" value={selected.reason ?? '—'} />
            <DrawerField label="Diajukan" value={formatDateTime(selected.created_at)} />
            {selected.decided_at && <DrawerField label="Diputuskan" value={formatDateTime(selected.decided_at)} />}
            {canApprove && selected.status === 'REQUESTED' && (
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <Button variant="primary" loading={decidingId === selected.swap_id} onClick={() => void handleDecide(selected.swap_id, true)}>
                  Setujui
                </Button>
                <Button variant="ghost" onClick={() => void handleDecide(selected.swap_id, false)}>
                  Tolak
                </Button>
              </div>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
}

function NewSwapForm({ employees, onCreated }: { employees: Employee[]; onCreated: () => void }) {
  const [requestingEmployeeId, setRequestingEmployeeId] = useState('');
  const [schedules, setSchedules] = useState<Schedule[] | null>(null);
  const [scheduleId, setScheduleId] = useState('');
  const [coveringEmployeeId, setCoveringEmployeeId] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSchedules(null);
    setScheduleId('');
    if (!requestingEmployeeId) return;
    listPlannedSchedules(requestingEmployeeId)
      .then(setSchedules)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [requestingEmployeeId]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!scheduleId) {
      setError('Pilih jadwal (schedule) yang ingin ditukar.');
      return;
    }
    if (coveringEmployeeId === requestingEmployeeId) {
      setError('Karyawan pengganti harus berbeda dari peminta.');
      return;
    }
    setSubmitting(true);
    try {
      await createShiftSwap(scheduleId, coveringEmployeeId, reason || null);
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="alex-card" style={{ marginBottom: 20, padding: 16, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 180 }}>
        <FieldLabel label="Peminta (jadwal saat ini)">
          <Select value={requestingEmployeeId} onChange={(e) => setRequestingEmployeeId(e.target.value)} required>
            <option value="" disabled>
              Pilih...
            </option>
            {employees.map((emp) => (
              <option key={emp.employee_id} value={emp.employee_id}>
                {emp.employee_no} — {emp.legal_name}
              </option>
            ))}
          </Select>
        </FieldLabel>
      </div>
      <div style={{ flex: 1, minWidth: 160 }}>
        <FieldLabel label="Jadwal">
          <Select value={scheduleId} onChange={(e) => setScheduleId(e.target.value)} required disabled={!requestingEmployeeId}>
            <option value="" disabled>
              {requestingEmployeeId ? 'Pilih...' : 'Pilih peminta dahulu'}
            </option>
            {schedules?.map((s) => (
              <option key={s.schedule_id} value={s.schedule_id}>
                {s.work_date}
              </option>
            ))}
          </Select>
        </FieldLabel>
        {requestingEmployeeId && schedules && schedules.length === 0 && (
          <div style={{ fontSize: 'var(--alex-fs-micro)', color: 'var(--alex-text-faint)', marginTop: 4 }}>Tidak ada jadwal PLANNED untuk karyawan ini.</div>
        )}
      </div>
      <div style={{ flex: 1, minWidth: 180 }}>
        <FieldLabel label="Pengganti">
          <Select value={coveringEmployeeId} onChange={(e) => setCoveringEmployeeId(e.target.value)} required>
            <option value="" disabled>
              Pilih...
            </option>
            {employees
              .filter((emp) => emp.employee_id !== requestingEmployeeId)
              .map((emp) => (
                <option key={emp.employee_id} value={emp.employee_id}>
                  {emp.employee_no} — {emp.legal_name}
                </option>
              ))}
          </Select>
        </FieldLabel>
      </div>
      <div style={{ flex: 1, minWidth: 160 }}>
        <FieldLabel label="Alasan (opsional)">
          <TextInput value={reason} onChange={(e) => setReason(e.target.value)} />
        </FieldLabel>
      </div>
      <Button type="submit" variant="primary" loading={submitting}>
        Ajukan
      </Button>
      {error && <ErrorNote text={error} />}
    </form>
  );
}
